import { useCallback, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { DEFAULT_RADIUS, REGION_RADIUS, CITY_REGION_RADIUS, getRadiusFromCityName } from '../helpers';

export const useCityInfo = () => {
  const geocoder = useMapsLibrary('geocoding');
  const cityRadiusCache = useRef(new Map<string, number>());

  const getCityRadiusFromCoordinates = useCallback(
    async (lat: number, lng: number): Promise<number> => {
      if (!geocoder || !lat || !lng) return DEFAULT_RADIUS;

      const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;

      if (cityRadiusCache.current.has(cacheKey)) {
        return cityRadiusCache.current.get(cacheKey) ?? DEFAULT_RADIUS;
      }

      return new Promise((resolve) => {
        const geocoderInstance = new window.google.maps.Geocoder();
        geocoderInstance.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results?.[0]) {
            const result = results[0];
            let radius = DEFAULT_RADIUS;

            const locality = result.address_components?.find((c) => c.types.includes('locality'));
            const adminLevel2 = result.address_components?.find((c) => c.types.includes('administrative_area_level_2'));
            const sublocality = result.address_components?.find((c) => c.types.includes('sublocality'));

            if (locality?.long_name) {
              radius = getRadiusFromCityName(locality?.long_name);
            } else if (adminLevel2) {
              radius = REGION_RADIUS;
            } else if (sublocality) {
              radius = CITY_REGION_RADIUS;
            }

            cityRadiusCache.current.set(cacheKey, radius);
            resolve(radius);
          } else {
            cityRadiusCache.current.set(cacheKey, DEFAULT_RADIUS);
            resolve(DEFAULT_RADIUS);
          }
        });
      });
    },
    [geocoder],
  );

  return { getCityRadiusFromCoordinates };
};
